/**
 * The four stacked canvases on screen (docs/03-ARCHITECTURE.md §7.1), with the pen
 * pipeline attached to them.
 *
 * The element keeps `touch-action: none` so two-finger pan and pinch reach our own
 * handlers rather than being swallowed by the browser (§7.3).
 */

import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';

import type { CanvasLayerName, ViewportState } from '@opal/contracts';
import { canDraw, createId, screenToCanvas, type Point, type Viewport } from '@opal/shared';

import {
  buildBoard,
  ERASER_RING_FALLBACK,
  ERASER_RING_TOKEN,
  SELECT_LINE_FALLBACK,
  SELECT_LINE_TOKEN,
  SELECT_WASH,
  type BoardHandle,
  type RingState,
} from './board-stack.js';
import { TextEditor } from '../components/board/TextEditor.js';
import { UndoSnapPill } from '../components/board/UndoSnapPill.js';
import { cursorFor, layerZIndex } from './cursor.js';
import { createInputHandlers } from './input-router.js';
import { cssColor, DEFAULT_PEN_COLOR, hexColor, inkColor } from './palette.js';
import { useBoardStore, type BoardState } from './store.js';
import { LAYER_ORDER, type CanvasLayers } from './layers.js';
import { attachNavigation, type Navigation } from './navigation.js';
import { attachPointerInput } from './pointer-input.js';
import { attachTouchGestures, type TouchGestures } from './touch-gestures.js';
import { createViewportState } from './viewport-state.js';

export type { BoardHandle } from './board-stack.js';

export interface BoardCanvasProps {
  /** Who is drawing. Every stroke is stamped with it (docs/05-DATA-MODEL.md §3.1). */
  readonly userId?: string;
  /** Called once the layers exist, so the screen can hang sharing off them. */
  readonly onReady?: (board: BoardHandle) => void;
  /** This person's hand, in canvas coordinates. Null when it leaves the board. */
  readonly onPointerMove?: (point: Point | null) => void;
}

/**
 * Follows the mouse or the stylus, so the overlay can draw the size ring under it.
 *
 * A finger is skipped on purpose: it is already covering the spot it is pointing at,
 * and a ring under it would be nothing but clutter.
 */
function usePointerRing(
  frame: HTMLDivElement | null,
  layers: MutableRefObject<CanvasLayers | null>,
  ring: MutableRefObject<RingState>,
  viewport: MutableRefObject<Viewport>,
  onPointerMove: BoardCanvasProps['onPointerMove'],
): void {
  useEffect(() => {
    if (!frame) return undefined;
    const repaint = (): void => layers.current?.scheduler.invalidate('overlay');
    const show = (event: PointerEvent): void => {
      const box = frame.getBoundingClientRect();
      const at = { x: event.clientX - box.left, y: event.clientY - box.top };
      // Everyone else sees a finger too — it is only the ring under it that would be
      // clutter on the screen the finger is already covering.
      onPointerMove?.(screenToCanvas(viewport.current, at));
      if (event.pointerType === 'touch') return;
      ring.current.at = at;
      repaint();
    };
    const hide = (): void => {
      onPointerMove?.(null);
      ring.current.at = null;
      repaint();
    };
    frame.addEventListener('pointermove', show);
    frame.addEventListener('pointerdown', show);
    frame.addEventListener('pointerleave', hide);
    return () => {
      frame.removeEventListener('pointermove', show);
      frame.removeEventListener('pointerdown', show);
      frame.removeEventListener('pointerleave', hide);
      hide();
    };
  }, [frame, layers, ring, viewport, onPointerMove]);
}

/** Wheel, Ctrl+wheel, space-drag and middle-mouse drag move the view (SRS C2). */
function useNavigation(frame: HTMLDivElement | null): MutableRefObject<Navigation | null> {
  const navigation = useRef<Navigation | null>(null);

  useEffect(() => {
    if (!frame) return undefined;
    const store = useBoardStore.getState();
    navigation.current = attachNavigation(frame, {
      onPan: store.panBy,
      onZoom: (factor, anchor) => {
        store.zoomAtPoint(useBoardStore.getState().viewport.zoom * factor, anchor);
      },
    });
    return () => {
      navigation.current?.detach();
      navigation.current = null;
    };
  }, [frame]);

  return navigation;
}

/** Two fingers on a touchscreen pan or pinch the board (SRS C2.1). */
function useTouchGestures(
  frame: HTMLDivElement | null,
  board: MutableRefObject<BoardHandle | null>,
): MutableRefObject<TouchGestures | null> {
  const gestures = useRef<TouchGestures | null>(null);

  useEffect(() => {
    if (!frame) return undefined;
    const store = useBoardStore.getState();
    gestures.current = attachTouchGestures(frame, {
      onPan: store.panBy,
      onZoom: (factor, anchor) => {
        store.zoomAtPoint(useBoardStore.getState().viewport.zoom * factor, anchor);
      },
      onStart: () => {
        // The first finger may have started a line. Two fingers means it was a
        // gesture all along, so that accidental mark is dropped.
        board.current?.ink.cancel();
        board.current?.erase.cancel();
        board.current?.select.cancel();
        board.current?.shape.cancel();
        board.current?.text.cancel();
        board.current?.laser.cancel();
      },
      onEnd: () => undefined,
    });
    return () => {
      gestures.current?.detach();
      gestures.current = null;
    };
  }, [frame, board]);

  return gestures;
}

/** Sends what the pen, finger or mouse does into the tool currently in hand. */
function useDrawing(
  frame: HTMLDivElement | null,
  board: MutableRefObject<BoardHandle | null>,
  viewport: MutableRefObject<Viewport>,
  navigation: MutableRefObject<Navigation | null>,
  gestures: MutableRefObject<TouchGestures | null>,
): void {
  useEffect(() => {
    const handle = board.current;
    if (!frame || !handle) return undefined;
    const view = (): ViewportState => createViewportState(viewport.current);
    return attachPointerInput(frame, {
      // SRS D4 — a student who has not been granted drawing leaves no mark at all,
      // and takes nothing off the board either.
      canDraw: () =>
        canDraw(useBoardStore.getState().access) &&
        !(navigation.current?.panMode() ?? false) &&
        !(gestures.current?.active() ?? false),
      viewport: view,
      ...createInputHandlers({
        ink: handle.ink,
        erase: handle.erase,
        select: handle.select,
        shape: handle.shape,
        text: handle.text,
        laser: handle.laser,
        tool: () => useBoardStore.getState().tool,
        viewport: view,
      }),
    });
  }, [frame, board, viewport, navigation, gestures]);
}

/** Keeps the layers sized to the element and to the screen's pixel ratio. */
function useCanvasSize(
  frame: HTMLDivElement | null,
  onSize: (widthPx: number, heightPx: number, dpr: number) => void,
): void {
  useEffect(() => {
    if (!frame) return undefined;
    const measure = (): void => {
      const box = frame.getBoundingClientRect();
      onSize(box.width, box.height, window.devicePixelRatio);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [frame, onSize]);
}

/** Creates the layers and the ink session, and keeps them following the store. */
function useBoardStack(
  canvases: MutableRefObject<Map<CanvasLayerName, HTMLCanvasElement>>,
  viewport: MutableRefObject<Viewport>,
  layers: MutableRefObject<CanvasLayers | null>,
  session: MutableRefObject<BoardHandle | null>,
  ring: MutableRefObject<RingState>,
  userId: string,
  onReady: BoardCanvasProps['onReady'],
): void {
  useEffect(() => {
    // Resolved here, not at first render: by now the stylesheet has certainly applied.
    ring.current.color = inkColor(useBoardStore.getState().color);
    ring.current.eraserColor = cssColor(ERASER_RING_TOKEN, ERASER_RING_FALLBACK);
    ring.current.select = {
      line: cssColor(SELECT_LINE_TOKEN, SELECT_LINE_FALLBACK),
      wash: SELECT_WASH,
    };
    const board = buildBoard(canvases.current, viewport.current, ring, userId);
    const { layers: stack } = board;
    layers.current = stack;
    session.current = board;
    useBoardStore.getState().setContentBounds(() => board.contentBounds());
    useBoardStore.getState().setSelection(board.select);
    useBoardStore.getState().setWriting(board.text);
    // What is chosen, and where it is, is drawn on the overlay; the panel only needs
    // to know how much of it there is.
    const stopWatchingSelection = board.select.onChanged(() => {
      stack.scheduler.invalidate('overlay');
      useBoardStore
        .getState()
        .setSelectionState(
          board.select.selection.length,
          board.select.grouped,
          board.select.copied,
        );
    });
    onReady?.(board);

    const unsubscribe = followStore(board, viewport, ring);

    return () => {
      unsubscribe();
      stopWatchingSelection();
      useBoardStore.getState().setContentBounds(null);
      useBoardStore.getState().setSelection(null);
      useBoardStore.getState().setWriting(null);
      board.laser.dispose();
      stack.dispose();
      layers.current = null;
      session.current = null;
    };
  }, [canvases, viewport, layers, session, ring, userId, onReady]);
}

/**
 * Keeps the canvas following the store.
 *
 * Zoom, pan, paper and tool changes arrive this way rather than through a React
 * render: the canvas has to repaint without the whole board screen re-rendering
 * around it, or a moving hand would drag the entire page along behind it.
 */
function followStore(
  board: BoardHandle,
  viewport: MutableRefObject<Viewport>,
  ring: MutableRefObject<RingState>,
): () => void {
  const stack = board.layers;
  let paper = useBoardStore.getState().background;
  let pen = penRingKey(useBoardStore.getState());
  let tool = useBoardStore.getState().tool;

  return useBoardStore.subscribe((state) => {
    if (state.background !== paper) {
      paper = state.background;
      stack.scheduler.invalidate('background');
    }
    if (state.tool !== tool) {
      tool = state.tool;
      // Putting the select tool down lets go of whatever it was holding, so the box
      // does not sit over the board while the teacher is writing.
      if (tool !== 'select') board.select.clear();
      // Reaching for another tool finishes whatever was being typed, exactly as
      // clicking away from it does.
      if (tool !== 'text' && tool !== 'sticky') board.text.commit();
    }
    // A thicker or differently coloured pen changes the ring without the hand moving.
    if (penRingKey(state) !== pen) {
      pen = penRingKey(state);
      ring.current.color = inkColor(state.color);
      stack.scheduler.invalidate('overlay');
    }
    if (state.viewport === viewport.current) return;
    viewport.current = state.viewport;
    stack.setViewport(createViewportState(state.viewport));
  });
}

/** What the ring under the hand depends on, beyond where the hand is. */
function penRingKey(state: BoardState): string {
  return `${state.tool}:${state.targetScreenWidth}:${state.color}:${state.eraserScreenWidth}`;
}

export function BoardCanvas({ userId, onReady, onPointerMove }: BoardCanvasProps) {
  const [frame, setFrame] = useState<HTMLDivElement | null>(null);
  const canvases = useRef(new Map<CanvasLayerName, HTMLCanvasElement>());
  const viewport = useRef<Viewport>(useBoardStore.getState().viewport);
  const layers = useRef<CanvasLayers | null>(null);
  const session = useRef<BoardHandle | null>(null);
  // A real colour from the start, so the ring can never be asked to paint a token name.
  const ring = useRef<RingState>({
    at: null,
    color: hexColor(DEFAULT_PEN_COLOR),
    eraserColor: ERASER_RING_FALLBACK,
    select: { line: SELECT_LINE_FALLBACK, wash: SELECT_WASH },
  });
  const tool = useBoardStore((state) => state.tool);
  // One id per tab is the fallback: every stroke needs an owner, even offline.
  const owner = useRef(userId ?? createId('usr')).current;

  useBoardStack(canvases, viewport, layers, session, ring, owner, onReady);

  const navigation = useNavigation(frame);
  const gestures = useTouchGestures(frame, session);
  useDrawing(frame, session, viewport, navigation, gestures);
  usePointerRing(frame, layers, ring, viewport, onPointerMove);

  // Stable identity: a new callback each render would rebuild the ResizeObserver and
  // repaint all four layers on every render.
  const onSize = useCallback((widthPx: number, heightPx: number, dpr: number) => {
    useBoardStore.getState().setSize(widthPx, heightPx);
    viewport.current = useBoardStore.getState().viewport;
    layers.current?.resize(createViewportState(viewport.current), dpr);
  }, []);

  useCanvasSize(frame, onSize);

  return (
    <div
      ref={setFrame}
      className="canvas-surface relative h-full w-full overflow-hidden"
      style={{ cursor: cursorFor(tool) }}
    >
      {LAYER_ORDER.map((name) => (
        <canvas
          key={name}
          data-layer={name}
          ref={(element) => {
            if (element) canvases.current.set(name, element);
            else canvases.current.delete(name);
          }}
          className="pointer-events-none absolute left-0 top-0"
          style={{ zIndex: layerZIndex(name, tool) }}
        />
      ))}
      <TextEditor />
      {/* SRS C5 — the way back from a snap, for the four seconds it is offered. */}
      <UndoSnapPill />
    </div>
  );
}
